import { useState, useEffect, useCallback } from "react";
import { LifeBuoy, Plus, Send, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { timeAgo, clsx } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

const statusCls = (s) => s === "open" ? "text-brandblue bg-brandblue/15" : s === "pending" ? "text-gold bg-gold/15" : "text-fx-s3 bg-white/5";

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "General", priority: "Normal", message: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/support/tickets").then((r) => setTickets(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openThread = async (id) => {
    setActive(id);
    const { data } = await api.get(`/support/tickets/${id}`);
    setThread(data);
  };

  const createTicket = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/support/tickets", form);
      toast.success("Ticket created");
      setShowNew(false); setForm({ subject: "", category: "General", priority: "Normal", message: "" });
      await load(); openThread(data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    await api.post(`/support/tickets/${active}/reply`, { message: reply });
    setReply("");
    openThread(active); load();
  };

  return (
    <div className="p-4 lg:p-8 space-y-6 max-w-[1200px]">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2"><LifeBuoy className="h-6 w-6 text-brandblue" /><h1 className="font-display text-3xl font-extrabold tracking-tight">Support</h1></div>
        <Button onClick={() => setShowNew(true)} data-testid="new-ticket-btn" className="bg-brandblue hover:bg-blue-500 gap-2"><Plus className="h-4 w-4" /> New Ticket</Button>
      </div>

      <div className="grid lg:grid-cols-[340px_1fr] gap-6">
        <div className="rounded-xl bg-fx-s1 border border-white/5 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/5 text-sm font-semibold">My Tickets</div>
          <div className="divide-y divide-white/[0.03] max-h-[60vh] overflow-y-auto">
            {tickets.length === 0 && <div className="px-4 py-8 text-center text-fx-s3 text-sm">No tickets yet.</div>}
            {tickets.map((t) => (
              <button key={t.id} onClick={() => openThread(t.id)} data-testid={`ticket-${t.id}`}
                className={clsx("w-full text-left px-4 py-3 transition-colors", active === t.id ? "bg-brandblue/10" : "hover:bg-white/[0.03]")}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium truncate">{t.subject}</span>
                  <span className={clsx("px-2 py-0.5 rounded text-[10px] font-mono shrink-0", statusCls(t.status))}>{t.status}</span>
                </div>
                <div className="text-xs text-fx-s3 mt-1">{t.category} · {t.priority} · {timeAgo(t.updated_at)}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl bg-fx-s1 border border-white/5 flex flex-col min-h-[400px]">
          {!thread ? (
            <div className="flex-1 grid place-items-center text-fx-s3 text-sm">Select a ticket to view the conversation.</div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                <div><div className="font-display font-bold">{thread.ticket.subject}</div><div className="text-xs text-fx-s3">{thread.ticket.category} · {thread.ticket.priority}</div></div>
                <span className={clsx("px-2 py-0.5 rounded text-[11px] font-mono", statusCls(thread.ticket.status))}>{thread.ticket.status}</span>
              </div>
              <div className="flex-1 p-5 space-y-3 overflow-y-auto max-h-[45vh]" data-testid="ticket-thread">
                {thread.messages.map((m) => (
                  <div key={m.id} className={clsx("max-w-[80%] rounded-lg p-3", m.sender_role === "staff" ? "bg-brandblue/15 border border-brandblue/20 ml-auto" : "bg-fx-base border border-white/5")}>
                    <div className="text-[10px] uppercase tracking-wider text-fx-s3 mb-1">{m.sender_role === "staff" ? "Support" : m.sender_name} · {timeAgo(m.created_at)}</div>
                    <div className="text-sm">{m.message}</div>
                  </div>
                ))}
              </div>
              {thread.ticket.status !== "closed" && (
                <div className="p-4 border-t border-white/5 flex gap-2">
                  <Input value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendReply()} data-testid="reply-input" placeholder="Type your reply…" className="bg-fx-base border-white/10" />
                  <Button onClick={sendReply} data-testid="reply-send-btn" className="bg-brandblue hover:bg-blue-500 shrink-0"><Send className="h-4 w-4" /></Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-fx-s2 border-white/10 text-white" data-testid="new-ticket-dialog">
          <DialogHeader><DialogTitle className="font-display">New Support Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Subject</Label><Input data-testid="ticket-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="bg-fx-base border-white/10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="ticket-category" className="bg-fx-base border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-fx-s2 border-white/10 text-white">{["General", "Payments", "Trading", "Bots", "Account", "KYC"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="ticket-priority" className="bg-fx-base border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-fx-s2 border-white/10 text-white">{["Low", "Normal", "High", "Urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1"><Label>Message</Label>
              <textarea data-testid="ticket-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className="w-full rounded-md bg-fx-base border border-white/10 p-3 text-sm resize-none" placeholder="Describe your issue…" />
            </div>
          </div>
          <DialogFooter><Button onClick={createTicket} disabled={busy} data-testid="ticket-create-btn" className="bg-brandblue hover:bg-blue-500 gap-2">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create ticket"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
